import type { MailAddress } from "@catmail/shared";
import { Power, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";

import { Button, Dialog, ListRow, TextField } from "@/components";

/**
 * 내 주소 하나를 손보는 창.
 *
 * 여는 길은 프로필 팝오버의 주소 목록이다. 예전에는 그 목록이 **보여주기만**
 * 했다 — 서버에는 끄는 길도 지우는 길도 진작 있었는데 화면이 부르지 않아서,
 * 잘못 신청한 주소를 스스로 치울 방법이 없었다.
 *
 * 팝오버 안에서 처리하지 않고 창을 따로 띄우는 이유: 지우기는 되돌릴 수 없고
 * **그 주소로 온 대화까지 함께 사라진다.** 좁은 화면에서 팝오버 위에 팝오버를
 * 겹치면 무엇을 지우는지 가려지기까지 한다.
 */

type ManageAddressDialogProps = {
  /** 손볼 주소. null이면 창이 닫힌 것이다 */
  address: MailAddress | null;
  onOpenChange: (open: boolean) => void;
  pending?: boolean;
  error?: string | null;
  onSetEnabled: (identityId: string, enabled: boolean) => void;
  onRemove: (identityId: string) => void;
};

export function ManageAddressDialog({
  address,
  onOpenChange,
  pending = false,
  error,
  onSetEnabled,
  onRemove,
}: ManageAddressDialogProps) {
  /** 지우기를 누른 뒤의 확인 단계. 창을 또 띄우지 않고 이 안에서 갈아 끼운다. */
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState("");

  useEffect(() => {
    if (!address) {
      setConfirming(false);
      setTyped("");
    }
  }, [address]);

  if (!address) return null;

  const enabled = address.enabled !== false;
  const waiting = address.status === "pending";
  const refused = address.status === "rejected";
  const localPart = address.address.split("@")[0] ?? "";

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <Dialog.Content size="sm">
        <Dialog.Close />

        <div className="flex flex-col gap-4 p-5">
          <div className="flex min-w-0 flex-col gap-1">
            <Dialog.Title className="truncate">{address.address}</Dialog.Title>
            <Dialog.Description>
              {waiting
                ? "받아들여지기를 기다리는 중이에요. 그 전에는 이 주소로 온 메일이 들어오지 않아요."
                : refused
                  ? "받아들여지지 않은 주소예요. 이 주소로 온 메일은 들어오지 않아요."
                  : address.isPrimary
                    ? "로그인에 쓰는 주소예요."
                    : enabled
                      ? "메일을 받고 있어요."
                      : "꺼둔 주소예요. 지금은 메일이 들어오지 않아요."}
            </Dialog.Description>
          </div>

          {confirming ? (
            /*
              지우기는 되돌릴 수 없고 대화까지 데려간다. 그래서 단추 하나로
              끝내지 않고 **주소를 직접 치게** 한다 — 손이 미끄러져 눌린 것과
              정말로 지우려는 것을 가르는 유일한 방법이다.
            */
            <>
              <p className="text-danger text-[13px]">
                이 주소로 주고받은 대화가 함께 사라져요. 되돌릴 수 없어요. 지우려면
                아래에 <span className="font-semibold">{localPart}</span> 를
                쳐주세요.
              </p>

              <TextField
                label="주소 확인"
                autoFocus
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                placeholder={localPart}
                value={typed}
                onChange={(event) => setTyped(event.target.value)}
              />

              {error ? <p className="text-danger text-[13px]">{error}</p> : null}

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setConfirming(false)}
                >
                  그만두기
                </Button>
                <Button
                  type="button"
                  variant="danger"
                  disabled={pending || typed.trim().toLowerCase() !== localPart}
                  onClick={() => onRemove(address.id)}
                >
                  {pending ? "지우는 중…" : "지우기"}
                </Button>
              </div>
            </>
          ) : (
            <>
              {error ? <p className="text-danger text-[13px]">{error}</p> : null}

              <div className="border-line-soft -mx-5 border-t pt-1.5">
                {/*
                  대표 주소는 끌 수도 지울 수도 없다. 끄면 로그인할 수 없게 되고
                  지우면 계정이 갈 곳을 잃는다. 서버도 같은 이유로 막는다
                  (routes/identities.ts) — 눌러 보고 나서 거절당하는 것보다,
                  왜 안 되는지 그 자리에서 말해주는 편이 낫다.
                */}
                {address.isPrimary ? (
                  <p className="text-muted-3 px-5 py-2 text-[13px]">
                    대표 주소는 끄거나 지울 수 없어요. 바꾸려면 받아주는 사람에게
                    말해주세요.
                  </p>
                ) : (
                  <>
                    <ListRow
                      size="sm"
                      leading={<Power />}
                      disabled={pending || waiting || refused}
                      onClick={() => onSetEnabled(address.id, !enabled)}
                    >
                      {enabled ? "이 주소 끄기" : "이 주소 다시 켜기"}
                    </ListRow>
                    <ListRow
                      size="sm"
                      tone="danger"
                      leading={<Trash2 />}
                      disabled={pending}
                      onClick={() => setConfirming(true)}
                    >
                      이 주소 지우기
                    </ListRow>
                  </>
                )}
              </div>

              {/*
                끄기와 지우기가 나란히 있으면 무엇이 다른지 헷갈린다. 끈 것은
                되돌아오고 지운 것은 안 돌아온다 — 그 차이만 적는다.
              */}
              {!address.isPrimary ? (
                <p className="text-muted-3 text-[13px]">
                  끄면 메일만 안 들어오고 대화는 그대로 남아요. 다시 켜면 돌아와요.
                </p>
              ) : null}
            </>
          )}
        </div>
      </Dialog.Content>
    </Dialog>
  );
}
